import React, { useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Trash2, Loader2, TriangleAlert } from "lucide-react";

const WHAT = {
  lead: "case",
  lender_application: "lender application",
  introducer_application: "introducer application",
  enquiry: "enquiry",
  invoice: "invoice",
  career_application: "careers application",
};

/**
 * PurgeButton — a hard delete with a receipt. Type DELETE, it's gone from every view,
 * and a line lands in the deletion log with your name on it.
 */
export function PurgeButton({ kind, ids = [], onDone, label, variant = "outline", className = "", testId }) {
  const [open, setOpen] = useState(false);
  const [confirm, setConfirm] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const n = ids.length;
  const noun = WHAT[kind] || "record";

  const go = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/hub/purge", { kind, ids, reason: reason.trim() || null });
      const extra = Object.values(data.cascade || {}).reduce((a, b) => a + b, 0);
      toast.success(`Deleted ${data.deleted} ${noun}${data.deleted === 1 ? "" : "s"}${extra ? ` and ${extra} attached record${extra === 1 ? "" : "s"}` : ""}`);
      setOpen(false); setConfirm(""); setReason("");
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    } finally { setBusy(false); }
  };

  return (
    <>
      <Button variant={variant} size="sm" disabled={!n} onClick={() => setOpen(true)}
              className={`rounded-[10px] h-8 text-[12px] text-red-600 hover:text-red-700 hover:border-red-300 ${className}`}
              data-testid={testId || `purge-${kind}`}>
        <Trash2 className="size-3.5 mr-1.5" /> {label || (n > 1 ? `Delete ${n}` : "Delete")}
      </Button>

      <Dialog open={open} onOpenChange={(o) => { if (!busy) { setOpen(o); setConfirm(""); } }}>
        <DialogContent className="sm:max-w-md" data-testid="purge-dialog">
          <DialogHeader>
            <DialogTitle className="text-[17px]">
              Delete {n} {noun}{n === 1 ? "" : "s"}?
            </DialogTitle>
            <DialogDescription className="text-[13px]">
              They go to the Bin for {30} days — restore them whole from the Bin tab if you change your mind.
              {kind === "lead" && " Files, notes, document requests, messages and CRM traces attached to the case go with them, and the case disappears from both partner portals immediately."}
            </DialogDescription>
          </DialogHeader>

          <div className="flex items-start gap-2.5 rounded-xl border border-[#7da9ff]/45 bg-[#7da9ff]/[0.10] p-3 text-[12.5px] text-[#24457F]">
            <TriangleAlert className="size-4 shrink-0 mt-0.5 text-[#3B63A8]" />
            <span>A line is written to the deletion log with your name, the time and what was removed — and the record itself waits in the Bin, so a delete can always be accounted for and undone.</span>
          </div>

          <div className="space-y-3">
            <div>
              <div className="text-[10.5px] font-semibold uppercase tracking-[0.14em] text-slate-600">Reason (optional)</div>
              <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Hoax application, duplicate, test data…"
                     className="mt-1.5 h-9" data-testid="purge-reason" />
            </div>
            <div>
              <div className="text-[10.5px] font-semibold uppercase tracking-[0.14em] text-slate-600">Type DELETE to confirm</div>
              <Input value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="DELETE"
                     className="mt-1.5 h-9 font-mono" data-testid="purge-confirm" />
            </div>
          </div>

          <div className="flex items-center justify-end gap-2">
            <Button variant="outline" size="sm" className="h-9 rounded-[10px]" onClick={() => setOpen(false)} disabled={busy}
                    data-testid="purge-cancel">Cancel</Button>
            <Button size="sm" onClick={go} disabled={busy || confirm.trim().toUpperCase() !== "DELETE"}
                    className="h-9 rounded-[10px] bg-red-600 hover:bg-red-700 text-white" data-testid="purge-go">
              {busy ? <Loader2 className="size-3.5 animate-spin" /> : `Delete ${n} ${noun}${n === 1 ? "" : "s"}`}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
